import React from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FilterX } from 'lucide-react';

const ALL = '__all__';
const ETAPAS = [
  { value: 'Borrador', label: 'Propuesta (Borrador)', match: ['Borrador'] },
  { value: 'En Negociación', label: 'En Negociación', match: ['En Negociación'] },
  { value: 'Enviada', label: 'Enviada', match: ['Enviada'] },
  { value: 'Primer Pago', label: 'Primer Pago', match: ['Primer Pago'] },
  { value: 'Aceptada', label: 'Aceptada', match: ['Aceptada', 'Ejecución/Activa'] },
  { value: 'Rechazada', label: 'Rechazada', match: ['Rechazada'] },
];
const ATAJOS = [
  { value: ALL, label: 'Cualquier fecha' },
  { value: 'hoy', label: 'Hoy' },
  { value: '7', label: 'Últimos 7 días' },
  { value: '30', label: 'Últimos 30 días' },
  { value: 'anio', label: 'Este año' },
  { value: 'rango', label: 'Rango personalizado' },
];
const EMPTY_DATE = { atajo: ALL, desde: '', hasta: '' };
export const EMPTY_FILTERS = { etapa: ALL, owner: ALL, creacion: EMPTY_DATE, actividad: EMPTY_DATE };

export const getOwner = (p) => p.created_by || p.bitacora_comercial?.[0]?.usuario || 'Sin propietario';
export const getLastActivity = (p) => {
  const fechas = (p.bitacora_comercial || []).map(b => b.fecha).filter(Boolean);
  if (p.updated_at) fechas.push(p.updated_at);
  if (!fechas.length) return p.created_at || null;
  return fechas.reduce((a, b) => (new Date(a) > new Date(b) ? a : b));
};

const matchDate = (value, f) => {
  if (f.atajo === ALL) return true;
  if (!value) return false;
  const d = new Date(value);
  const now = new Date();
  if (f.atajo === 'hoy') return d.toDateString() === now.toDateString();
  if (f.atajo === '7' || f.atajo === '30') return d <= now && now - d <= Number(f.atajo) * 86400000;
  if (f.atajo === 'anio') return d.getFullYear() === now.getFullYear();
  if (f.desde && d < new Date(`${f.desde}T00:00:00`)) return false;
  if (f.hasta && d > new Date(`${f.hasta}T23:59:59`)) return false;
  return true;
};

export const applyVentasFilters = (proposals, f) => proposals
  .filter(p => f.etapa === ALL || ETAPAS.find(e => e.value === f.etapa)?.match.includes(p.status))
  .filter(p => f.owner === ALL || getOwner(p) === f.owner)
  .filter(p => matchDate(p.created_at, f.creacion))
  .filter(p => matchDate(getLastActivity(p), f.actividad));

const DateFilter = ({ label, value, onChange, testid }) => (
  <div className="space-y-1">
    <span className="text-xs font-medium text-slate-600">{label}</span>
    <Select value={value.atajo} onValueChange={(atajo) => onChange({ ...value, atajo })}>
      <SelectTrigger className="h-9 text-xs" data-testid={`${testid}-atajo`}><SelectValue /></SelectTrigger>
      <SelectContent>
        {ATAJOS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
      </SelectContent>
    </Select>
    {value.atajo === 'rango' && (
      <div className="flex gap-1">
        <Input type="date" className="h-8 text-xs" value={value.desde} onChange={(e) => onChange({ ...value, desde: e.target.value })} data-testid={`${testid}-desde`} />
        <Input type="date" className="h-8 text-xs" value={value.hasta} onChange={(e) => onChange({ ...value, hasta: e.target.value })} data-testid={`${testid}-hasta`} />
      </div>
    )}
  </div>
);

export const VentasFiltros = ({ filtros, onChange, owners = [] }) => {
  const set = (k) => (v) => onChange({ ...filtros, [k]: v });
  return (
    <Card className="p-4 mb-4" data-testid="ventas-filtros">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-start">
        <div className="space-y-1">
          <span className="text-xs font-medium text-slate-600">Etapa</span>
          <Select value={filtros.etapa} onValueChange={set('etapa')}>
            <SelectTrigger className="h-9 text-xs" data-testid="lista-filter-etapa"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todas</SelectItem>
              {ETAPAS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <span className="text-xs font-medium text-slate-600">Propietario</span>
          <Select value={filtros.owner} onValueChange={set('owner')}>
            <SelectTrigger className="h-9 text-xs" data-testid="lista-filter-propietario"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos</SelectItem>
              {owners.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <DateFilter label="Fecha creación" value={filtros.creacion} onChange={set('creacion')} testid="lista-filter-creacion" />
        <DateFilter label="Última actividad" value={filtros.actividad} onChange={set('actividad')} testid="lista-filter-actividad" />
        <Button variant="outline" size="sm" className="h-9 md:mt-5" onClick={() => onChange(EMPTY_FILTERS)} data-testid="lista-reset-filters">
          <FilterX className="w-4 h-4 mr-1" /> Limpiar filtros
        </Button>
      </div>
    </Card>
  );
};
